import { forwardRef } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { Button as MuiButton } from '@mui/material'
import { styled } from '@mui/material/styles'

import { elementRefType } from '~/src/Lib/PropTypes'
import { EMPTY_OBJECT } from '~/src/Lib/Utils/constants'
import getClassPrefixer from '~/src/UI/classPrefixer'

const displayName = 'Aroya/Button'
const classes = getClassPrefixer(displayName)

const StyledButton = styled(MuiButton, { name: 'AroyaButton' })(({ theme }) => ({
  [`&.${classes.hero}`]: {
    fontSize: 18,
    fontFamily: theme.typography.boldFontFamily,
    padding: theme.spacing(1.5, 4),
  },
  [`&.${classes.primaryAction}`]: {
    minWidth: '14rem',
    padding: theme.spacing(0.75, 4),
  },
}))

/**
 * The Button component wraps and extends the @mui/material Button component.
 *
 * @example
 *   {() => {
 *   const [clicked, setClicked] = React.useState(null)
 *   const buttonOnClick = ({ target }) => {
 *   setClicked(target.closest('button').textContent)
 *   }
 *   return (
 *   <>
 *   {clicked ? <Box textAlign="center" fontSize="2rem">clicked: {clicked}</Box> : null}
 *   <Box
 *   display="grid"
 *   alignContent="center"
 *   alignItems="center"
 *   justifyContent="space-around"
 *   gridTemplateColumns="repeat(auto-fill, minmax(14rem, 1fr))"
 *   gap="1rem"
 *   >
 *   <Button hero onClick={buttonOnClick}>hero button</Button>
 *   <Button variant="text" onClick={buttonOnClick}>text button</Button>
 *   <Button primaryAction onClick={buttonOnClick}>primary action</Button>
 *   <Button color="default" onClick={buttonOnClick}>default color</Button>
 *   <Button color="secondary" onClick={buttonOnClick}>secondary color</Button>
 *   <Button color="error" hero onClick={buttonOnClick}>error hero</Button>
 *   </Box>
 *   </>
 *   )
 *   }}
 *
 * @component
 * @alias Button
 * @composes
 * from: '@mui/material/Button'
 * link: https://material-ui.com/api/button/#props
 */
const ButtonComponent = ({
  buttonRef = null,
  children,
  className,
  color = 'primary',
  hero = false,
  muiClasses = EMPTY_OBJECT,
  primaryAction = false,
  ...passthru
}) => {
  return (
    <StyledButton
      classes={muiClasses}
      color={color}
      variant="contained"
      {...passthru}
      className={classNames(className, {
        [classes.hero]: hero,
        [classes.primaryAction]: primaryAction,
      })}
      ref={buttonRef}
    >
      {children}
    </StyledButton>
  )
}
ButtonComponent.displayName = 'Button'
ButtonComponent.propTypes = {
  buttonRef: elementRefType,
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
  hero: PropTypes.bool,
  muiClasses: PropTypes.objectOf(PropTypes.string),
  /** Make the button match Aroya design-spec primary page action padding and button width */
  primaryAction: PropTypes.bool,
}

const RefForwarding = forwardRef((props, ref) => <ButtonComponent {...props} buttonRef={ref} />)

export default RefForwarding
